import type { Database, Tables } from './database.types.js';

export type Idea = Tables<'ideas'>;
export type Doc = Tables<'docs'>;
export type DocVersion = Tables<'doc_versions'>;
export type DocTemplate = Tables<'doc_templates'>;
export type IdeaStatus = Database['public']['Enums']['idea_status'];
export type DocKind = Database['public']['Enums']['doc_kind'];

/** Idea statuses, in the order an idea moves through them (as in NeuroForge). */
export const IDEA_STATUSES: readonly { id: IdeaStatus; name: string }[] = [
  { id: 'spark', name: 'Spark' },
  { id: 'exploring', name: 'Exploring' },
  { id: 'designing', name: 'Designing' },
  { id: 'planned', name: 'Planned' },
  { id: 'building', name: 'Building' },
  { id: 'shelved', name: 'Shelved' },
];

export const IDEA_STATUS_IDS = IDEA_STATUSES.map((s) => s.id) as [IdeaStatus, ...IdeaStatus[]];

/**
 * Doc kinds, in tab order, with the title a new doc gets and the file it came
 * from in NeuroForge (and in templates/). Custom docs have no template.
 */
export const DOC_KINDS: readonly { id: DocKind; title: string; file: string | null }[] = [
  { id: 'overview', title: 'Overview', file: 'overview.md' },
  { id: 'concept', title: 'Concept', file: 'concept.md' },
  { id: 'mechanics', title: 'Mechanics', file: 'mechanics.md' },
  { id: 'world', title: 'World', file: 'world.md' },
  { id: 'art-direction', title: 'Art direction', file: 'art-direction.md' },
  { id: 'audio', title: 'Audio', file: 'audio.md' },
  { id: 'tech', title: 'Tech', file: 'tech.md' },
  { id: 'scope', title: 'Scope', file: 'scope.md' },
  { id: 'build-plan', title: 'Build plan', file: 'BUILD_PLAN.md' },
  { id: 'custom', title: 'Notes', file: null },
];

export const DOC_KIND_IDS = DOC_KINDS.map((k) => k.id) as [DocKind, ...DocKind[]];

export function docKindTitle(kind: DocKind): string {
  return DOC_KINDS.find((k) => k.id === kind)?.title ?? kind;
}

/** Sorts docs into tab order: template kinds in DOC_KINDS order, then custom docs by title. */
export function compareDocs(a: Pick<Doc, 'kind' | 'title'>, b: Pick<Doc, 'kind' | 'title'>): number {
  const order = (k: DocKind) => DOC_KIND_IDS.indexOf(k);
  return order(a.kind) - order(b.kind) || a.title.localeCompare(b.title);
}

/** Fills a template's {{title}} and {{date}} (yyyy-mm-dd). */
export function fillTemplate(body: string, values: { title: string; date: string }): string {
  return body.replace(/\{\{\s*title\s*\}\}/g, values.title).replace(/\{\{\s*date\s*\}\}/g, values.date);
}

/** SQLSTATE save_doc() raises when the doc has moved on since the editor's version. */
export const DOC_CONFLICT_CODE = 'HV409';

/** How long an "is editing" flag counts after its last heartbeat. */
export const EDITING_STALE_MS = 2 * 60_000;

export function isEditingNow(doc: Pick<Doc, 'editing_by' | 'editing_since'>, now = Date.now()): boolean {
  return !!doc.editing_by && !!doc.editing_since && now - Date.parse(doc.editing_since) < EDITING_STALE_MS;
}
